import {Client} from "@/api/dto/client-profile";
import {Message} from "@/api/dto/message";
import {StateConfig} from "@/api/dto/state-config";
import {BasicRosterResponse} from "@/api/dto/dto";
import {colors} from "patient-ping-remedy/packages/theme";

export type NotificationFrequencyType = "REAL_TIME" | "HOURLY"
  | "THREE_HOURS" | "DAILY";

export const REFERRAL_EVENT = "REFERRAL";
export const CRISIS_EVENT = "CRISIS";
export const HOSPITAL_EVENT = "HOSPITAL";
export const JI_EVENT = "JI";
export const POST_ACUTE_CARE_EVENT = "POST_ACUTE_CARE";
export const FIND_HELP_EVENT = "FIND_HELP";
export const TASK_EVENT = "TASKS";
export const MESSAGE_MENTION = "MESSAGE_MENTION";
export const CRISIS_SERVICES_EVENT = "CRISIS_SERVICES";
export const PROMISE_SERVICES_EVENT = "PROMISE_SERVICES";
export const BED_AVAILABILITY_EVENT = "BED_AVAILABILITY";
export const ADDITIONAL_EVENT = "ADDITIONAL_EVENT";
export const ADDITIONAL_EVENT_RECORDED_SUBTYPE = "ADDITIONAL_EVENT_RECORDED";

export const OVERDUE_NOTIFICATION_SUBTYPES: string[] = [
  "EXCEED_REFERRAL",
  "ESCALATE_REFERRAL",
  "BED_AVAILABILITY_NOT_UPDATED",
  "BED_AVAILABILITY_ESCALATION",
  "OUTPATIENT_APPOINTMENT_NOT_UPDATED",
  "OUTPATIENT_APPOINTMENT_ESCALATION",
];

export type NotificationMetadata = { message: string; url: string; urlSuffix: string; dataType?: string }

export const parseNotificationMetadata = (metadata?: string): NotificationMetadata => {
  try {
    return JSON.parse(metadata ?? "");
  } catch {
    return {message: "-", url: "/", urlSuffix: ""};
  }
};

export const resolveAdditionalEventDataType = (metadata?: string): string | undefined => {
  const {dataType} = parseNotificationMetadata(metadata);
  return dataType?.trim();
};

export type NotificationEventType =
  typeof REFERRAL_EVENT |
  typeof CRISIS_EVENT |
  typeof HOSPITAL_EVENT |
  typeof JI_EVENT |
  typeof POST_ACUTE_CARE_EVENT |
  typeof FIND_HELP_EVENT |
  typeof MESSAGE_MENTION |
  typeof TASK_EVENT |
  typeof CRISIS_SERVICES_EVENT |
  typeof PROMISE_SERVICES_EVENT |
  typeof BED_AVAILABILITY_EVENT |
  typeof ADDITIONAL_EVENT;

export type NotificationEventTypeNatural =
  typeof REFERRAL_EVENT |
  typeof CRISIS_EVENT |
  typeof HOSPITAL_EVENT |
  typeof JI_EVENT |
  typeof POST_ACUTE_CARE_EVENT |
  typeof FIND_HELP_EVENT |
  typeof TASK_EVENT |
  typeof CRISIS_SERVICES_EVENT |
  typeof PROMISE_SERVICES_EVENT;

export type NotificationEventTypeBedAvailability =
    typeof BED_AVAILABILITY_EVENT;

export type NotificationEventTypeExtended =
    NotificationEventTypeNatural | NotificationEventTypeBedAvailability;

export const NotificationEventTypeSettingList: NotificationEventType[] = [
  REFERRAL_EVENT,
  CRISIS_EVENT,
  HOSPITAL_EVENT,
  JI_EVENT,
  POST_ACUTE_CARE_EVENT,
  FIND_HELP_EVENT,
  CRISIS_SERVICES_EVENT,
  PROMISE_SERVICES_EVENT
];

export const WorkflowNotificationTypeSettingList: NotificationEventType[] = [
  TASK_EVENT
];

export const BedAvailabilityNotificationTypeSettingList: NotificationEventType[] = [
  BED_AVAILABILITY_EVENT
];

export const NotificationEventTypeNaturalList: NotificationEventType[] = [
  ...NotificationEventTypeSettingList,
  ...WorkflowNotificationTypeSettingList,
];

export const NotificationEventTypeAllList: NotificationEventType[] = [
  ...NotificationEventTypeNaturalList,
  ...BedAvailabilityNotificationTypeSettingList,
  ADDITIONAL_EVENT,
];

export const resolveNotificationIconBackground = (
  notificationType: NotificationEventType
): string | undefined => {
  if (!NotificationEventTypeAllList.includes(notificationType)) {
    return undefined;
  }
  return notificationType === ADDITIONAL_EVENT ? colors.primary1 : colors.red5;
};

export type NotificationEventSettingType =
  HospitalSetting | ReferralSetting | CrisisSetting | JISetting | PacSetting | FindHelpSetting | TasksSetting
    | CrisisServicesSetting | PromiseServicesSetting;

export type NotificationEventSettingTypeExtended = NotificationEventSettingType | BedAvailabilitySetting;

export type NotificationFormInfoType = typeof HospitalFormInfo | typeof ReferralFormInfo
  | typeof CrisisFormInfo | typeof JIFormInfo | typeof PacFormInfo | typeof FindHelpFormInfo | typeof TasksInfo
    | typeof CrisisServicesFormInfo | typeof PromiseServicesFormInfo;

export type NotificationFormInfoTypeExtended = NotificationFormInfoType | typeof BedAvailabilityFormInfo;

export interface NotificationDto {
  id: number;
  notificationType: NotificationEventType
  notificationSubtype: string
  client: Client
  message?: Message
  group: BasicRosterResponse
  unitName?: string
  notificationDate: string
  metadata?: string;
  viewed: boolean
}

export interface NotificationResponse {
  totalElements: number
  hasMore: boolean
  notifications: NotificationDto[]
}

export interface NotificationSettingsDto {
  notificationEventSettingDtos: { [key: string]: NotificationEventSettingDto };
  notificationScheduleDto: NotificationScheduleDto | null;
}

export interface NotificationEventSettingDto {
  enabled: boolean;
  notificationType: NotificationEventTypeExtended;
  emailEnabled: boolean;
  smsEnabled: boolean;
  notificationSubtypes: { [key: string]: boolean };
}

export interface NotificationScheduleDto {
  fromTime: string
  toTime: string
  timezone: string
  notificationFrequency: string
  customScheduledEnabled: boolean
  notificationScheduleDays: string[]
}
export interface NotificationSettings {
  data: NotificationSettingsDto;
  referralSetting: ReferralSetting
  crisisSetting: CrisisSetting
  findHelpSetting: FindHelpSetting
  hospitalSetting: HospitalSetting
  jiSetting: JISetting
  pacSetting: PacSetting
  crisisServicesSetting: CrisisServicesSetting
  promiseServicesSetting: PromiseServicesSetting
  notificationSchedule: NotificationSchedule
  taskSetting: TasksSetting
  bedAvailabilitySetting: BedAvailabilitySetting
}

export interface NotificationSetting {
  enabled: boolean
  notificationType: NotificationEventTypeExtended
  emailEnabled: boolean
  smsEnabled: boolean
}

export interface ReferralSetting extends NotificationSetting {
  newReferral: boolean
  accepted: boolean
  declined: boolean
  show: boolean
  noShow: boolean
  acceptedElsewhere: boolean
  pending: boolean
  waitlisted: boolean
  anticipatedDateOverdue: boolean
  scheduledDateOverdue: boolean
  messageAlerting: boolean
}

export interface CrisisSetting extends NotificationSetting {
  intakeCompleted: boolean
  dispatchCanceled: boolean
  dispatchRequested: boolean
  dispatchAccepted: boolean
  dispatchDeclined: boolean
  dispatchArrived: boolean
  dispatchDeparted: boolean
  dispatchAcknowledged: boolean
  intakeCanceled: boolean
}

export interface FindHelpSetting extends NotificationSetting {
  newReferral: boolean
  accepted: boolean
  declined: boolean
  pending: boolean
}

export interface TasksSetting extends NotificationSetting {
  created: boolean
  assignmentChanged: boolean
  overdue: boolean
  updated: boolean,
  rescheduleNeeded: boolean
  additionalEvent: boolean
}

export interface HospitalSetting extends NotificationSetting {
  admitted: boolean
  presentedEr: boolean
  discharged: boolean
  transferred: boolean
  deceased: boolean
}

export interface JISetting extends NotificationSetting {
  booked: boolean
  released: boolean
}

export interface PacSetting extends NotificationSetting {
  admitted: boolean
  discharge: boolean
  transferred: boolean
  deceased: boolean
}

export interface CrisisServicesSetting extends NotificationSetting {
    admitted: boolean
    discharge: boolean
    transferred: boolean
    deceased: boolean
}

export interface PromiseServicesSetting extends NotificationSetting {
    admitted: boolean
    discharge: boolean
    transferred: boolean
    deceased: boolean
}

export interface NotificationSchedule {
  fromTime: Date
  toTime: Date
  timezone: string
  notificationFrequency: NotificationFrequencyType
  customScheduledEnabled: boolean
  notificationScheduleDays: NotificationScheduleDays
}

export interface NotificationScheduleDays {
  mon: boolean
  tue: boolean
  wed: boolean
  thu: boolean
  fri: boolean
  sat: boolean
  sun: boolean
}

export type NotificationFormInfo = {
  label: string;
  code: string;
}

export type ReferralInfoType = { [K in keyof Partial<ReferralSetting>]: NotificationFormInfo };

export const ReferralFormInfo: ReferralInfoType = {
  newReferral: {
    label: 'New referral',
    code: 'NEW_REFERRAL',
  },
  accepted: {
    label: 'Referral accepted',
    code: 'REFERRAL_ACCEPTED',
  },
  declined: {
    label: 'Referral declined',
    code: 'REFERRAL_DECLINED',
  },
  show: {
    label: 'Client showed',
    code: 'REFERRAL_SHOW',
  },
  noShow: {
    label: 'Client no showed',
    code: 'REFERRAL_NO_SHOW',
  },
  acceptedElsewhere: {
    label: 'Referral accepted elsewhere',
    code: 'REFERRAL_ACCEPTED_ELSEWHERE',
  },
  pending: {
    label: 'Referral pending',
    code: 'REFERRAL_PENDING',
  },
  waitlisted: {
    label: 'Referral waitlisted',
    code: 'REFERRAL_WAITLISTED',
  },
  anticipatedDateOverdue: {
    label: 'Referral anticipated date overdue',
    code: 'REFERRAL_ANTICIPATED_DATE_OVERDUE',
  },
  scheduledDateOverdue: {
    label: 'Referral scheduled date overdue',
    code: 'REFERRAL_SCHEDULED_DATE_OVERDUE',
  },
  messageAlerting: {
    label: 'Message Alerting?',
    code: 'MESSAGE_ALERTING',
  },
};

export type CrisisInfoType = { [K in keyof Partial<CrisisSetting>]: NotificationFormInfo };

export const CrisisFormInfo: CrisisInfoType = {
  intakeCompleted: {
    label: 'Intake completed',
    code: 'CRISIS_INTAKE_COMPLETED',
  },
  intakeCanceled: {
    label: 'Intake canceled',
    code: 'CRISIS_INTAKE_CANCELED',
  },
  dispatchRequested: {
    label: 'Mobile dispatch requested',
    code: 'CRISIS_DISPATCH_REQUESTED',
  },
  dispatchAcknowledged: {
    label: 'Mobile dispatch acknowledged',
    code: 'CRISIS_DISPATCH_ACKNOWLEDGED',
  },
  dispatchAccepted: {
    label: 'Mobile dispatch accepted',
    code: 'CRISIS_DISPATCH_ACCEPTED',
  },
  dispatchDeclined: {
    label: 'Mobile dispatch declined',
    code: 'CRISIS_DISPATCH_DECLINED',
  },
  dispatchArrived: {
    label: 'Mobile dispatch arrived',
    code: 'CRISIS_DISPATCH_ARRIVED',
  },
  dispatchDeparted: {
    label: 'Mobile dispatch departed',
    code: 'CRISIS_DISPATCH_DEPARTED',
  },
  dispatchCanceled: {
    label: 'Mobile dispatch canceled',
    code: 'CRISIS_DISPATCH_CANCELED',
  }
};

export type HospitalInfoType = { [K in keyof Partial<HospitalSetting>]: NotificationFormInfo };

export const HospitalFormInfo: HospitalInfoType= {
  presentedEr: {
    label: 'Presented',
    code: 'PRESENTED',
  },
  admitted: {
    label: 'Admitted',
    code: 'ADMITTED',
  },
  transferred: {
    label: 'Transferred',
    code: 'TRANSFERRED',
  },
  discharged: {
    label: 'Discharged',
    code: 'DISCHARGED',
  },
  deceased: {
    label: 'Deceased',
    code: 'DECEASED',
  },


};

export type JIInfoType = { [K in keyof Partial<JISetting>]: NotificationFormInfo};

export const JIFormInfo: JIInfoType = {
  booked: {
    label: 'Booked',
    code: 'BOOKED',
  },
  released: {
    label: 'Released',
    code: 'RELEASED',
  }
};

export type PacInfoType = { [K in keyof Partial<PacSetting>]: NotificationFormInfo};

export const PacFormInfo: PacInfoType = {
  admitted: {
    label: 'Admitted',
    code: 'ADMITTED',
  },
  discharge: {
    label: 'Discharged',
    code: 'DISCHARGED',
  },
  transferred: {
    label: 'Transferred',
    code: 'TRANSFERRED',
  },
  deceased: {
    label: 'Deceased',
    code: 'DECEASED',
  }
};

export type FindHelpInfoType = { [K in keyof Partial<FindHelpSetting>]: NotificationFormInfo};

export const FindHelpFormInfo: FindHelpInfoType = {
  newReferral: {
    label: 'New referral',
    code: 'NEW_REFERRAL',
  },
  pending: {
    label: 'Referral pending',
    code: 'REFERRAL_PENDING',
  },
  accepted: {
    label: 'Referral accepted',
    code: 'REFERRAL_ACCEPTED',
  },
  declined: {
    label: 'Referral declined',
    code: 'REFERRAL_DECLINED',
  }

};

export type TasksInfoType = { [K in keyof Partial<TasksSetting>]: NotificationFormInfo};

export const TasksInfo: TasksInfoType = {
  created: {
    label: 'Task Created',
    code: 'TASK_CREATED',
  },
  assignmentChanged: {
    label: 'Task Assignment Changed',
    code: 'TASK_ASSIGNMENT_CHANGED',
  },
  overdue: {
    label: 'Task Overdue',
    code: 'TASK_OVERDUE',
  },
  updated: {
    label: 'Task Update',
    code: 'TASK_UPDATED',
  },
  rescheduleNeeded: {
    label: 'Reschedule Needed',
    code: 'TASK_APPOINTMENT_REBOOK_NEEDED',
  },
  additionalEvent: {
    label: 'Additional Events',
    code: 'ADDITIONAL_EVENT_RECORDED',
  }
};


export type CrisisServicesInfoType = { [K in keyof Partial<PacSetting>]: NotificationFormInfo};

export const CrisisServicesFormInfo: CrisisServicesInfoType = {
  admitted: {
    label: 'Admitted',
    code: 'ADMITTED',
  },
  discharge: {
    label: 'Discharged',
    code: 'DISCHARGED',
  },
  transferred: {
    label: 'Transferred',
    code: 'TRANSFERRED',
  },
  deceased: {
    label: 'Deceased',
    code: 'DECEASED',
  }
};

export type PromiseServicesInfoType = { [K in keyof Partial<PacSetting>]: NotificationFormInfo};

export const PromiseServicesFormInfo: PromiseServicesInfoType = {
  admitted: {
    label: 'Admitted',
    code: 'ADMITTED',
  },
  discharge: {
    label: 'Discharged',
    code: 'DISCHARGED',
  },
  transferred: {
    label: 'Transferred',
    code: 'TRANSFERRED',
  },
  deceased: {
    label: 'Deceased',
    code: 'DECEASED',
  }
};

export interface BedAvailabilitySetting extends NotificationSetting {
  bedAvailabilityNotUpdated: boolean
  bedAvailabilityEscalation: boolean
  outpatientAppointmentNotUpdated: boolean
  outpatientAppointmentEscalation: boolean
}

export type BedAvailabilityInfoType = { [K in keyof Partial<BedAvailabilitySetting>]: NotificationFormInfo};

export const BedAvailabilityFormInfo: BedAvailabilityInfoType = {
  bedAvailabilityNotUpdated: {
    label: 'Bed Availability Not Updated',
    code: 'BED_AVAILABILITY_NOT_UPDATED',
  },
  bedAvailabilityEscalation: {
    label: 'Bed Availability Escalation',
    code: 'BED_AVAILABILITY_ESCALATION',
  },
  outpatientAppointmentNotUpdated: {
    label: 'Next Available Appointment Not Updated',
    code: 'OUTPATIENT_APPOINTMENT_NOT_UPDATED',
  },
  outpatientAppointmentEscalation: {
    label: 'Next Available Appointment Escalation',
    code: 'OUTPATIENT_APPOINTMENT_ESCALATION',
  },
};

export type DayOfWeekInfoType = { [K in keyof NotificationScheduleDays]: NotificationFormInfo};

export const dayOfWeekFormInfo: DayOfWeekInfoType = {
  mon: {
    label: 'Mon',
    code: 'MONDAY',
  },
  tue: {
    label: 'Tue',
    code: 'TUESDAY',
  },
  wed: {
    label: 'Wed',
    code: 'WEDNESDAY',
  },
  thu: {
    label: 'Thu',
    code: 'THURSDAY',
  },
  fri: {
    label: 'Fri',
    code: 'FRIDAY',
  },
  sat: {
    label: 'Sat',
    code: 'SATURDAY',
  },
  sun: {
    label: 'Sun',
    code: 'SUNDAY',
  }
};

export type NotificationFrequencyInfoType = {[key in NotificationFrequencyType]: string};

export const notificationFrequencyInfo: NotificationFrequencyInfoType = {
  REAL_TIME: "In Real Time",
  HOURLY: "Every Hour",
  THREE_HOURS: "Every 3 Hours",
  DAILY: "Once Daily"
};

export type NotificationInfo = {
  drawerHeaderText: string,
  profileLabelText: string,
  notificationFieldName: keyof Partial<NotificationSettings>,
  eventFormInfo: NotificationFormInfoTypeExtended
}

export type NotificationInfoType = { [key in NotificationEventTypeExtended]: NotificationInfo; };

export const NotificationInfoMap = (stateConfig: StateConfig | undefined): NotificationInfoType => ({
  REFERRAL: {
    drawerHeaderText: `${stateConfig?.branding.openBeds.referral.event} event settings`,
    profileLabelText: `${stateConfig?.branding.openBeds.referral.event} events`,
    notificationFieldName: 'referralSetting',
    eventFormInfo: ReferralFormInfo
  },
  CRISIS: {
    drawerHeaderText: `${stateConfig?.branding.openBeds.crisis.event} event settings`,
    profileLabelText: `${stateConfig?.branding.openBeds.crisis.event} events`,
    notificationFieldName: 'crisisSetting',
    eventFormInfo: CrisisFormInfo
  },
  HOSPITAL: {
    drawerHeaderText: 'Hospital event settings',
    profileLabelText: 'Hospital events',
    notificationFieldName: 'hospitalSetting',
    eventFormInfo: HospitalFormInfo
  },
  JI: {
    drawerHeaderText: 'Justice Involved event settings',
    profileLabelText: 'Justice Involved events',
    notificationFieldName: 'jiSetting',
    eventFormInfo: JIFormInfo
  },
  POST_ACUTE_CARE: {
    drawerHeaderText: 'Post acute care event settings',
    profileLabelText: 'Post acute care events',
    notificationFieldName: 'pacSetting',
    eventFormInfo: PacFormInfo
  },
  FIND_HELP: {
    drawerHeaderText: `${stateConfig?.branding.findHelp.event} event settings`,
    profileLabelText: `${stateConfig?.branding.findHelp.event} events`,
    notificationFieldName: 'findHelpSetting',
    eventFormInfo: FindHelpFormInfo
  },
  TASKS: {
    drawerHeaderText: `Task settings`,
    profileLabelText: `Tasks`,
    notificationFieldName: 'taskSetting',
    eventFormInfo: TasksInfo
  },
  CRISIS_SERVICES: {
    drawerHeaderText: `Crisis Services event settings`,
    profileLabelText: `Crisis Services events`,
    notificationFieldName: 'crisisServicesSetting',
    eventFormInfo: CrisisServicesFormInfo
  },
  PROMISE_SERVICES: {
    drawerHeaderText: `Promise Services event settings`,
    profileLabelText: `Promise Services events`,
    notificationFieldName: 'promiseServicesSetting',
    eventFormInfo: PromiseServicesFormInfo
  },
  BED_AVAILABILITY: {
    drawerHeaderText: `Bed Availability settings`,
    profileLabelText: `Bed Availability`,
    notificationFieldName: 'bedAvailabilitySetting',
    eventFormInfo: BedAvailabilityFormInfo
  }
});

export const NotificationDisplayText: {
  [key in NotificationEventType]: {
    [key: string]: (notification: NotificationDto) => string
  }
} = {
  REFERRAL: {
    'NEW_REFERRAL': () => 'New Referral',
    'EXCEED_REFERRAL': () => 'New referral',
    'ESCALATE_REFERRAL': () => 'New referral',
    'REFERRAL_ACCEPTED': () => 'Referral Accepted',
    'REFERRAL_DECLINED': () => 'Referral Declined',
    'REFERRAL_SHOW': () => 'Referral Done - Client Showed',
    'REFERRAL_NO_SHOW': () => 'Referral Done - Client No Showed',
    'REFERRAL_ACCEPTED_ELSEWHERE': () => 'Referral Accepted Elsewhere',
    'REFERRAL_PENDING': () => 'Referral Pending',
    'REFERRAL_WAITLISTED': () => 'Referral Waitlisted',
    'REFERRAL_ANTICIPATED_DATE_OVERDUE': () => 'Anticipated Availability Overdue',
    'REFERRAL_SCHEDULED_DATE_OVERDUE': () => 'Scheduled Date Overdue',
    'MESSAGE_ALERTING': () => 'Referral message'
  },
  CRISIS: {
    'CRISIS_INTAKE_COMPLETED': () => 'Crisis intake completed',
    'CRISIS_DISPATCH_CANCELED': () => 'Crisis dispatch canceled',
    'CRISIS_DISPATCH_REQUESTED': () => 'Crisis dispatch requested',
    'CRISIS_DISPATCH_ACCEPTED': () => 'Crisis dispatch accepted',
    'CRISIS_DISPATCH_DECLINED': () => 'Crisis dispatch declined',
    'CRISIS_DISPATCH_ARRIVED': () => 'Crisis dispatch arrived',
    'CRISIS_DISPATCH_DEPARTED': () => 'Crisis dispatch departed',
    'CRISIS_DISPATCH_ACKNOWLEDGED': () => 'Crisis dispatch acknowledged',
    'CRISIS_INTAKE_CANCELED': () => 'Crisis intake canceled',
  },
  HOSPITAL: {
    'ADMITTED': () => 'Hospital admitted',
    'PRESENTED': () => 'Hospital presented',
    'DISCHARGED': () => 'Hospital discharged',
    'TRANSFERRED': () => 'Hospital transferred',
    'DECEASED': () => 'Hospital deceased'
  },
  JI: {
    'BOOKED': () => 'Justice Involved booked',
    'RELEASED': () => 'Justice Involved released'
  },
  POST_ACUTE_CARE: {
    'ADMITTED': () => 'Post acute admitted',
    'DISCHARGED': () => 'Post acute discharged',
    'TRANSFERRED': () => 'Post acute transferred',
    'DECEASED': () => 'Post acute deceased'
  },
  FIND_HELP: {
    'NEW_REFERRAL': () => 'New referral',
    'REFERRAL_ACCEPTED': () => 'Referral accepted',
    'REFERRAL_DECLINED': () => 'Referral declined',
    'REFERRAL_PENDING': () => 'Referral pending'
  },
  MESSAGE_MENTION: {
    'UNKNOWN': (notification) => `${notification.message?.user.first_name} ${notification.message?.user.last_name}`,
  },
  TASKS: {
    'TASK_CREATED': () => 'Task Created',
    'TASK_ASSIGNMENT_CHANGED': () => 'Task Assignment Changed',
    'TASK_OVERDUE': () => 'Task Overdue',
    'TASK_UPDATED': () => 'Task Update',
    'TASK_APPOINTMENT_REBOOK_NEEDED': () => 'Reschedule Needed'
  },
  CRISIS_SERVICES: {
    'ADMITTED': () => 'Crisis Services admitted',
    'DISCHARGED': () => 'Crisis Services discharged',
    'TRANSFERRED': () => 'Crisis Services transferred',
    'DECEASED': () => 'Crisis Services deceased'
  },
  PROMISE_SERVICES: {
    'ADMITTED': () => 'Promise Services admitted',
    'DISCHARGED': () => 'Promise Services discharged',
    'TRANSFERRED': () => 'Promise Services transferred',
    'DECEASED': () => 'Promise Services deceased'
  },
  BED_AVAILABILITY: {
    'BED_AVAILABILITY_NOT_UPDATED': () => 'Unit Update',
    'BED_AVAILABILITY_ESCALATION': () => 'Unit Update',
    'OUTPATIENT_APPOINTMENT_NOT_UPDATED': () => 'Unit Update',
    'OUTPATIENT_APPOINTMENT_ESCALATION': () => 'Unit Update',
  },
  ADDITIONAL_EVENT: {
    'ADDITIONAL_EVENT_RECORDED': (notification) => {
      const eventDataType = resolveAdditionalEventDataType(notification.metadata);
      return eventDataType
        ? `Additional ${eventDataType} event was recorded`
        : 'Additional event recorded';
    },
  }
};

export enum NotificationAlert {
  NOTIFICATION_MARKED_AS_READ = 'Notification marked as read.',
  NOTIFICATION_MARKED_AS_UNREAD = 'Notification marked as unread.',
  ALL_NOTIFICATIONS_MARKED_AS_READ = 'All notifications marked as read.',
  FAILED_TO_MARK_NOTIFICATION = 'Failed to mark a notification as read.',
  FAILED_TO_MARK_ALL_NOTIFICATIONS = 'Failed to mark all notifications as read.',
}
